import type { ProblemReport } from '@ghp/ui'
import * as Sentry from '@sentry/react'

type DataCollection = NonNullable<NonNullable<Parameters<typeof Sentry.init>[0]>['dataCollection']>

/**
 * Ce que Sentry reçoit du back-office (D15) : la pile d'appels, l'écran, la version — et rien qui
 * nomme une personne. Les paramètres d'URL portent les recherches (un nom, une adresse), les
 * en-têtes et cookies la session. `satisfies Required<…>` : une catégorie qu'une montée de
 * version ajouterait ne compile pas tant que personne n'a décidé de son sort.
 */
const COLLECTED_DATA = {
  userInfo: false,
  cookies: false,
  httpHeaders: false,
  httpBodies: [],
  urlQueryParams: false,
  databaseQueryData: false,
  queues: false,
  stackFrameVariables: false,
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
  frameContextLines: 5,
} satisfies Required<DataCollection>

/**
 * Les traces jointes à une erreur ne passent pas par `dataCollection`. Une ligne de console peut
 * citer ce qu'elle affiche, un clic le libellé du bouton (« Ouvrir la fiche de M. Dupont »), une
 * adresse visitée la recherche tapée (`?q=Dupont`). Console et clics ne partent pas, les adresses
 * partent sans ce qui suit le chemin.
 */
export function withoutPersonalData(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  if (breadcrumb.category === 'console' || breadcrumb.category?.startsWith('ui.')) return null

  for (const key of ['url', 'from', 'to']) {
    const value: unknown = breadcrumb.data?.[key]
    if (typeof value === 'string')
      breadcrumb.data = { ...breadcrumb.data, [key]: value.split(/[?#]/)[0] }
  }
  return breadcrumb
}

/**
 * Sans `VITE_SENTRY_DSN` — en local, en test, en CI —, rien ne démarre et rien ne part.
 * `VITE_APP_VERSION` n'est posée que par le déploiement de production : c'est elle qui sépare la
 * production du reste dans Sentry.
 */
export function startErrorReporting(env: ImportMetaEnv): void {
  const dsn = env.VITE_SENTRY_DSN?.trim()
  if (!dsn) return

  Sentry.init({
    dsn,
    release: env.VITE_APP_VERSION,
    environment: env.VITE_APP_VERSION ? 'production' : 'dev',
    dataCollection: COLLECTED_DATA,
    beforeBreadcrumb: withoutPersonalData,
  })
}

/** Le bouton « Signaler un problème » ne s'affiche que là où ses tickets arrivent quelque part. */
export const isErrorReportingEnabled = (): boolean => Sentry.isEnabled()

/**
 * La page telle qu'elle est, sans le bouton qui ouvre le ticket. La bibliothèque se charge au
 * premier ticket : le JavaScript du démarrage n'en porte rien.
 */
export async function captureScreen(): Promise<string> {
  const { domToJpeg } = await import('modern-screenshot')
  // ponytail: JPEG 0,8 à l'échelle 1 — lisible, et léger pour un envoi qui peut attendre le réseau.
  return domToJpeg(document.documentElement, {
    quality: 0.8,
    scale: 1,
    filter: (node) => !(node instanceof Element && node.hasAttribute('data-report-trigger')),
  })
}

/**
 * Le ticket part dans la boîte de réception de Sentry (#481) : c'est elle que l'équipe — et une
 * IA, par le MCP Sentry — lit et traite. Il porte l'écran, l'app et sa version, le compte par son
 * identifiant seul, et la dernière erreur vue. L'adresse part sans recherche ni fragment (D15).
 * Les images ajoutées perdent leur nom de fichier, qui peut nommer quelqu'un (« Maison Dupont »).
 */
export async function sendReport(
  report: ProblemReport,
  accountId: string | undefined,
): Promise<'sent' | 'queued'> {
  const images = await Promise.all(report.images.map(imageAttachment))
  Sentry.captureFeedback(
    {
      message: report.message,
      source: 'report-button',
      url: location.origin + location.pathname,
      associatedEventId: Sentry.lastEventId(),
      tags: { kind: report.kind, account_id: accountId },
    },
    {
      attachments: [
        ...(report.screenshot === null ? [] : [jpegAttachment(report.screenshot)]),
        ...images,
      ],
    },
  )
  return 'sent'
}

function jpegAttachment(dataUrl: string) {
  const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1)
  const data = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0))
  return { filename: 'capture.jpg', data, contentType: 'image/jpeg' }
}

async function imageAttachment(image: File, index: number) {
  const extension = /\.[a-z0-9]+$/i.exec(image.name)?.[0].toLowerCase() ?? ''
  return {
    filename: `image-${index + 1}${extension}`,
    data: new Uint8Array(await image.arrayBuffer()),
    contentType: image.type,
  }
}
